import { motion } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { CheckCircle2, Sparkles, ArrowRight, Users, Goal, TrendingUp, User, Briefcase } from 'lucide-react'

interface OnboardingCompleteStepProps {
  employeeName: string
  isManager: boolean
  onComplete: () => void
}

export default function OnboardingCompleteStep({
  employeeName,
  isManager,
  onComplete
}: OnboardingCompleteStepProps) {
  const firstName = employeeName.split(' ')[0]

  // Employee steps - shown to everyone
  const employeeSteps = [
    {
      icon: Goal,
      title: 'Objectives Dashboard',
      description: 'Your people leader will schedule a collaborative session to set objectives together'
    },
    {
      icon: TrendingUp,
      title: 'My Impact & Growth Journey',
      description: 'Navigate your journey and track your progress'
    },
    {
      icon: Sparkles,
      title: 'Interactive Platform Tour',
      description: 'A guided tour is available to help you discover all features'
    }
  ]

  // Manager steps - only shown to managers
  const managerSteps = [
    {
      icon: Users,
      title: 'Team Management Dashboard',
      description: 'View your direct reports and their objectives'
    },
    {
      icon: Goal,
      title: 'Objective Setting Tools',
      description: 'Collaborate with team members on their goals'
    },
    {
      icon: TrendingUp,
      title: 'Team Analytics & Insights',
      description: 'Access comprehensive team progress data and trends'
    }
  ]

  return (
    <div className="max-w-2xl mx-auto text-center space-y-8">
      {/* Success Animation */}
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.2 }}
        className="flex justify-center"
      >
        <div className="relative">
          <div className="absolute inset-0 bg-emerald-400/30 rounded-full blur-2xl animate-pulse" />
          <div className="relative bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full p-6">
            <CheckCircle2 className="h-16 w-16 text-white" />
          </div>
        </div>
      </motion.div>

      {/* Title */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="space-y-3"
      >
        <h1 className="text-3xl font-bold text-heritage-blue">
          Onboarding Complete! 🎉
        </h1>
        <p className="text-lg text-gray-600">
          Welcome to the team, {firstName}! You're all set to start your impact & growth journey.
        </p>
      </motion.div>

      {/* Next Steps - Employee Section (shown to everyone) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="bg-gradient-to-br from-heritage-blue/5 to-future-blue/5 rounded-xl p-6 border border-heritage-blue/10"
      >
        <div className="flex items-center justify-center gap-2 mb-4">
          <User className="h-5 w-5 text-heritage-blue" />
          <h2 className="text-lg font-semibold text-heritage-blue">
            Available Features
          </h2>
        </div>
        <div className="space-y-4">
          {employeeSteps.map((step, index) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 + index * 0.1 }}
              className="flex items-start gap-4 text-left"
            >
              <div className="bg-white rounded-lg p-2 shadow-sm">
                <step.icon className="h-5 w-5 text-future-blue" />
              </div>
              <div>
                <h3 className="font-medium text-heritage-blue">{step.title}</h3>
                <p className="text-sm text-gray-600">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Next Steps - Manager Section (only for managers) */}
      {isManager && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-gradient-to-br from-future-blue/5 to-heritage-blue/5 rounded-xl p-6 border border-future-blue/10"
        >
          <div className="flex items-center justify-center gap-2 mb-4">
            <Briefcase className="h-5 w-5 text-future-blue" />
            <h2 className="text-lg font-semibold text-future-blue">
              People Leader Tools & Features
            </h2>
          </div>
          <div className="space-y-4">
            {managerSteps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 1.0 + index * 0.1 }}
                className="flex items-start gap-4 text-left"
              >
                <div className="bg-white rounded-lg p-2 shadow-sm">
                  <step.icon className="h-5 w-5 text-heritage-blue" />
                </div>
                <div>
                  <h3 className="font-medium text-heritage-blue">{step.title}</h3>
                  <p className="text-sm text-gray-600">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Action Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: isManager ? 1.4 : 1 }}
        className="space-y-3"
      >
        <Button
          onClick={onComplete}
          size="lg"
          className="bg-heritage-blue hover:bg-heritage-blue/90 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
        >
          Go to My Team Member Journey
          <ArrowRight className="ml-2 h-5 w-5" />
        </Button>
        {isManager && (
          <p className="text-sm text-gray-500">
            You can switch to your People Leader Hub from the sidebar at any time
          </p>
        )}
      </motion.div>

      {/* Helpful Tip */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: isManager ? 1.6 : 1.2 }}
        className="text-sm text-gray-500"
      >
        💡 Tip: Look for the "Take Demo Tour" button to get a guided walkthrough of the platform
      </motion.p>
    </div>
  )
}
